import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Store, FlaskConical, Tractor, Sprout, Microscope, FileCheck2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const ICONS = [
  [Leaf, "Clean Grown", "from-emerald-400 to-emerald-600"],
  [Store, "Dispensary Quality", "from-amber-400 to-orange-500"],
  [FlaskConical, "Independent Lab Testing", "from-purple-500 to-fuchsia-600"],
  [Tractor, "Farm Direct", "from-orange-500 to-rose-500"],
];

const STEPS = [
  [Sprout, "Pick your product.", "Choose the strains you want at the farm."],
  [Microscope, "We connect you with a lab.", "We point you to an independent testing lab and help you get samples submitted."],
  [FileCheck2, "Get your COA.", "You receive your own Certificate of Analysis before buying in bulk."],
];

export const ProductQuality = () => (
  <section className="py-24 sm:py-32" data-testid="product-quality-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-12 items-center">
      <Reveal className="lg:col-span-7">
        <p className="eyebrow text-primary">Product quality</p>
        <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Dispensary-Grade. Clean Grown. <span className="text-primary">Test It Yourself.</span></h2>
        <p className="mt-5 text-base sm:text-lg font-semibold leading-relaxed">This isn't a street dealer hoping to make a sale. This is real product from real farms, and we'll help you verify it.</p>
        <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
          Our farms grow clean, dispensary-quality flower at serious volume, with more strains than any single test report could cover. So we do it better: we help you get your own independent lab testing (COA) on the product you're buying. Your results, your lab, your peace of mind.
        </p>
        <Link to="/apply" data-testid="quality-apply-button" className="group mt-8 inline-flex h-14 items-center gap-2 px-8 rounded-full bg-primary text-primary-foreground font-bold btn-glow hover:-translate-y-1 transition-transform">
          Get Introduced – Apply Now <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Reveal>
      <div className="lg:col-span-5 grid grid-cols-2 gap-4">
        {ICONS.map(([Icon, label, color], i) => (
          <Reveal key={label} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow]" data-testid={`quality-icon-${i}`}>
              <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${color} grid place-items-center shadow-lg`}><Icon className="h-6 w-6 text-white" /></div>
              <p className="mt-4 font-display text-lg font-bold leading-tight">{label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const HowTestingWorks = () => (
  <section className="pb-4" data-testid="how-testing-works-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <p className="eyebrow text-purple-500">How testing works</p>
        <h2 className="mt-3 text-2xl sm:text-4xl font-bold tracking-tight">Your product. Your lab. Your COA.</h2>
      </Reveal>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {STEPS.map(([Icon, title, text], i) => (
          <Reveal key={title} delay={i * 0.1}>
            <div className="relative h-full rounded-3xl border border-border bg-card p-7 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow]" data-testid={`testing-step-${i + 1}`}>
              <span className="absolute top-6 right-6 font-display text-5xl font-extrabold text-muted-foreground/15">0{i + 1}</span>
              <div className="h-12 w-12 rounded-2xl bg-primary/15 grid place-items-center"><Icon className="h-6 w-6 text-primary" /></div>
              <p className="mt-5 text-xs font-bold uppercase tracking-widest text-muted-foreground">Step {i + 1}</p>
              <h3 className="mt-1 text-xl font-bold">{title}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-sm text-muted-foreground font-semibold" data-testid="testing-cost-note">Lab testing costs are paid by the buyer and are not included in membership pricing.</p>
    </div>
  </section>
);
